// The Email Workspace's rules (busy-my-web#16, steps 2-4).
//
// The journey Fatemeh described is: the Briefing summarises the inbox, "N email
// groups" expands to a checkbox list, ticking groups and pressing Archive shows
// "Emails are being archived" and then "Emails have been archived", with the
// Workspace and Briefing updating afterwards.
//
// Everything in here is the part of that which can be wrong without looking
// wrong: what the counts say, what a tick means, what gets sent to the archive
// endpoint, and what the list should look like once it comes back. The view
// renders these; it does not decide them.

import { countText } from '@/lib/briefings/rows'

/**
 * "14 email groups" — the lower-case phrase, kept for the sentences that say it mid-line (the
 * fallback summary: "…come from 14 email groups"). The Briefing card's own ENTRY writes it
 * capitalised since busy-my-web#603 (see EMAIL_ENTRIES); the count takes the separator either way.
 */
export function groupsLabel(count) {
  const n = Number(count) || 0
  return `${countText(n)} email group${n === 1 ? '' : 's'}`
}

/** "91 emails · 75 unread" under a group's name. */
export function groupSubtitle(group) {
  const total = Number(group?.totalCount) || 0
  const unseen = Number(group?.unseenCount) || 0
  const emails = `${total} email${total === 1 ? '' : 's'}`
  return unseen > 0 ? `${emails} · ${unseen} unread` : emails
}

/**
 * Ticking a group. Selection is by SENDER, not by index — the list re-sorts
 * itself after an archive, and an index-based selection would then be pointing
 * at somebody else's mail.
 */
export function toggleSender(selected, sender) {
  const next = new Set(selected || [])
  if (next.has(sender)) next.delete(sender)
  else next.add(sender)
  return next
}

/** How many emails the current selection would archive — what the button promises. */
export function selectedEmailCount(groups, selected) {
  const picked = new Set(selected || [])
  return (groups || [])
    .filter(g => g && picked.has(g.sender))
    .reduce((sum, g) => sum + (Number(g.totalCount) || 0), 0)
}

/** The Archive button's label, so it says what it is about to do. */
export function archiveButtonLabel(groups, selected) {
  const groupCount = (selected || []).size ?? (selected || []).length ?? 0
  if (!groupCount) return 'Archive'
  const emails = selectedEmailCount(groups, selected)
  return `Archive ${emails} email${emails === 1 ? '' : 's'}`
}

/** The request the archive endpoint expects, from the ticked senders. */
export function archiveRequest(selected) {
  return { senders: [...(selected || [])] }
}

// ── Delete, and undo it (acc-account-sync-srv#18 / busy-main-bff, 2026-09-15 parity audit) ──────
//
// "Delete selected groups N emails" is the one option on the Work-board that was drawn greyed since
// it was written, because there was no endpoint behind it (see the note at the head of
// lib/emailThreadActions.js). There is one now:
//
//   POST v1/email/deleteSenders  { senders, mainFolder? } → { matched, deleted, alreadyDeleted, unrecognisedByProvider }
//   POST v1/email/restoreSenders { senders, mainFolder? } → { matched, restored, notDeleted, unrecognisedByProvider }
//
// Same body both ways, which is what makes Undo possible at all: the receipt keeps the senders it
// deleted and hands them straight back.

/**
 * The body both routes take. `mainFolder` rides along ONLY when the workspace is actually scoped to
 * a folder — an absent scope must not be sent as a null, which a strict model binder reads as "the
 * folder whose id is nothing" rather than as "every folder".
 */
export function deleteRequest(selected, mainFolder = null) {
  const body = { senders: [...(selected || [])] }
  if (mainFolder !== null && mainFolder !== undefined && mainFolder !== '' && Number.isFinite(Number(mainFolder))) {
    body.mainFolder = Number(mainFolder)
  }
  return body
}

/**
 * The band over the middle while a delete runs and after it, in the archive band's own shape so the
 * one banner in EmailInboxPanel.vue draws both.
 *
 * Every count is read DEFENSIVELY off the answer: a BFF that has not shipped the field yet, or an
 * older shape, leaves the head at 0 rather than throwing, and `alreadyDeleted` is only mentioned
 * when it happened — "0 already were" is noise on every normal delete.
 *
 * `undo: true` on the settled receipt is what draws the Undo control. It is always offered, because
 * `restoreSenders` always exists: an Undo that is sometimes there and sometimes not is a control
 * nobody learns.
 */
export function deleteNotice(state, { requested = 0, result = null } = {}) {
  if (state === 'deleting') {
    return { kind: 'deleting', text: `Deleting ${requested} email${requested === 1 ? '' : 's'}...` }
  }
  if (state === 'deleted') {
    const deleted = Number(result?.deleted) || 0
    const already = Number(result?.alreadyDeleted) || 0
    const head = `${deleted} email${deleted === 1 ? '' : 's'} deleted`
    return {
      kind: 'deleted',
      tone: 'success',
      undo: true,
      text: already > 0 ? `${head} · ${already} already were` : head,
    }
  }
  if (state === 'restoring') {
    return { kind: 'restoring', text: `Restoring ${requested} email${requested === 1 ? '' : 's'}...` }
  }
  if (state === 'restored') {
    const restored = Number(result?.restored) || 0
    return { kind: 'restored', tone: 'success', text: `${restored} email${restored === 1 ? '' : 's'} restored` }
  }
  if (state === 'error') {
    return { kind: 'error', text: 'Those emails could not be deleted. Nothing was changed.' }
  }
  if (state === 'restore-error') {
    return { kind: 'error', text: 'Those emails could not be restored.' }
  }
  return null
}

/**
 * The notice shown while archiving and after (frames 4.3 and 4.4).
 *
 * The finished text reports what the SERVER said it archived rather than what
 * was asked for: a group can shrink between the list being drawn and the button
 * being pressed, and claiming to have archived 91 when the response said 89
 * would be the kind of small lie nobody ever catches.
 */
export function archiveNotice(state, { requested = 0, result = null } = {}) {
  if (state === 'archiving') {
    // Three dots, not an ellipsis: the frame (40008242:422388) writes "Archiving 733 emails..."
    // and this section's rule is the design's own strings (busy-my-web#99).
    return { kind: 'archiving', text: `Archiving ${requested} email${requested === 1 ? '' : 's'}...` }
  }
  if (state === 'archived') {
    const archived = Number(result?.archived) || 0
    const already = Number(result?.alreadyArchived) || 0
    const head = `${archived} email${archived === 1 ? '' : 's'} archived`
    // Worth saying out loud: a re-press that finds everything already gone
    // otherwise reports "0 emails archived" and looks like a failure.
    // GREEN, with a "View" that opens the archived mail (frame 40008242:423135, busy-my-web#494):
    // a receipt is a success, and the frame colours it as one. `tone` and `view` are read by the
    // banner every session notice rides (EmailInboxPanel.vue) — the running band stays indigo.
    return {
      kind: 'archived',
      tone: 'success',
      view: ARCHIVED_PATH,
      text: already > 0 ? `${head} · ${already} already were` : head,
    }
  }
  if (state === 'error') {
    return { kind: 'error', text: 'Those emails could not be archived. Nothing was changed.' }
  }
  return null
}

/**
 * The list after a successful archive: the archived senders are gone.
 *
 * Removed rather than re-fetched so the Workspace updates the moment the call
 * returns — and because a re-fetch would race the provider, which is still
 * catching up in Gmail when the response arrives.
 */
export function withoutSenders(groups, senders) {
  const gone = new Set(senders || [])
  return (groups || []).filter(g => g && !gone.has(g.sender))
}

/**
 * The Briefing's own numbers after an archive, so the panel does not keep
 * quoting a total that includes mail the user just cleared.
 */
export function briefingTotals(groups) {
  const list = (groups || []).filter(Boolean)
  return {
    groups: list.length,
    emails: list.reduce((sum, g) => sum + (Number(g.totalCount) || 0), 0),
    unread: list.reduce((sum, g) => sum + (Number(g.unseenCount) || 0), 0),
  }
}

/**
 * The Briefing's counts with `emailGroups` said by the SENDER clusters (Figma 40009470-209128,
 * design clarification 2026-09-02: email groups are system-made, grouped by sender/contact, not
 * user collections). The dashboard's own `emailGroups` counts the bff#24 collections, which the
 * Briefing no longer lists — so a row saying "4 email groups" over a listing of fourteen clusters
 * would be counting a thing you cannot open here.
 *
 * Only where the clusters are KNOWN (an array, even an empty one): before they land the dashboard's
 * number stands rather than a zero the strip would drop. Null counts stay null — no dashboard is
 * still no dashboard. A new object, never a mutation: the counts object is Vuex state.
 *
 * …and an EMPTY list while the groups call is still in flight is not known either (busy-my-web#607,
 * Saffet 2026-10-09: "not visible for around 20 seconds then it appears"). `briefing/openEmails`
 * declares the inbox with `[]` in the click's own tick (#339) and only then asks `v1/email/groups`,
 * so for the whole of that call the list was an array — "known" — and its length, 0, overwrote the
 * dashboard's own `emailGroups`; the card dropped a zero row, and the row popped in when the
 * clusters landed and pushed everything under it down. With `loading` set, an empty list leaves
 * the dashboard's number standing (and where that is absent too, the card draws the placeholder).
 */
/**
 * …and the number is the TRUE number of groups where the server stated it (`total`, from
 * `POST v1/email/groups/page`), never the length of the page in hand (Saffet 2026-10-28: "50 email
 * groups" was the page size). The list's length is only the fallback for a server that did not say.
 */
export function withSystemGroupCount(counts, groups, { loading = false, total = null } = {}) {
  if (!counts || typeof counts !== 'object' || Array.isArray(counts)) return counts ?? null
  if (!Array.isArray(groups)) return counts
  if (loading && !groups.filter(Boolean).length) return counts
  const known = total != null && Number.isFinite(Number(total))
  return { ...counts, emailGroups: known ? Math.max(0, Number(total)) : groups.filter(Boolean).length }
}

/**
 * A Briefing suggestion is only actionable if its sender is still on screen.
 * The model was given the groups, but the list moves on — an archive, or a
 * folder filter, can leave a suggestion pointing at nothing.
 */
export function actionableSuggestions(suggestions, groups) {
  const known = new Set((groups || []).filter(Boolean).map(g => g.sender))
  return (suggestions || []).filter(s => s && known.has(s.sender))
}

/** The workspace's own route — where the header's × is a NAVIGATION, not just a reset. */
export const WORKSPACE_PATH = '/emails/workspace'

/** The archived mail listing — what the archive receipt's "View" opens (busy-my-web#494 (b)). */
export const ARCHIVED_PATH = '/emails/archived'

/**
 * Where the header's × leaves you (busy-my-web#295).
 *
 * The session used to be closed IN PLACE: `emailSession/close` reset the state and the user stayed
 * on whatever page they were on. That was right while the workspace was an overlay opened from
 * home — the page underneath was still home. It stopped being right once home's Follow-up count
 * rows started ROUTING to `/emails/workspace`: closing then emptied the page it left the user
 * standing on, which reads as a broken workspace rather than a closed one.
 *
 * So on the workspace's own route the × goes home, and nowhere else changes: any other page that
 * ends a session (the SmartBox's Home button already routes itself) keeps the in-place close.
 * Returns null when there is nothing to navigate to — the caller pushes only for a path.
 */
export function closeDestination(path) {
  return String(path || '') === WORKSPACE_PATH ? '/home' : null
}
